'use client';

import type { UTCDate } from '@date-fns/utc';
import { ClockIcon } from '@heroicons/react/24/solid';
import Image from 'next/image';
import Link from 'next/link';
import { useGetGameFromRouteParams } from '@/hooks/useGetGameFromParams.hook';
import type { PoweredBy } from '@/types/sponsor';
import { getGameLogoFromGame } from '@/utils/gameLogos';
import { Countdown } from '../Countdown';
import { getRewardsForGame } from './rewards';

export const PickemsRewardsBanner = ({
  targetDate,
  poweredBy,
}: {
  targetDate: UTCDate;
  poweredBy: PoweredBy;
}) => {
  const { game } = useGetGameFromRouteParams();
  const rewards = getRewardsForGame(game);
  const logoHeight = 35;
  const logoWidth = (logoHeight / poweredBy.logo.height) * poweredBy.logo.width;

  return (
    <div className="flex flex-col items-center gap-6 rounded-lg bg-dark-gray p-4 md:p-6 lg:flex-row lg:justify-between lg:gap-0 lg:p-8">
      {/* Game Info Section */}
      <div className="flex w-full items-center gap-3 md:gap-5 lg:w-auto">
        <Image
          alt={game}
          className="w-16 object-contain md:w-20"
          height={60}
          src={getGameLogoFromGame(game)}
          width={60}
        />
        <div className="flex flex-col gap-2">
          <h1 className="font-semibold text-white text-xl md:text-2xl">
            Daily Pick EM&apos;s
          </h1>
          <p className="flex items-center gap-2 text-sm md:text-base">
            <ClockIcon className="w-4 fill-blue-gray md:w-5" />
            <span className="text-gray">
              <Countdown
                expiredElement={
                  <span className="text-red">Betting closed</span>
                }
                suffix="to lock in"
                targetDate={targetDate}
              />
            </span>
          </p>
        </div>
      </div>

      {/* Rewards Section */}
      <div className="flex w-full flex-col gap-4 lg:w-auto">
        <span className="text-left text-sm">Monthly rewards</span>

        <div className="flex flex-col gap-4 sm:flex-row sm:items-center md:gap-8">
          {/* Sponsor Section */}
          <div className="flex flex-col gap-1">
            <span className="text-gray text-xs">Sponsored by</span>
            <Link href={poweredBy.url} target="_blank">
              <Image
                alt={poweredBy.name}
                className="object-contain"
                height={logoHeight}
                src={poweredBy.logo.url}
                width={logoWidth}
              />
            </Link>
          </div>

          {/* Rewards Grid */}
          <div className="grid w-full grid-cols-3 gap-2 sm:w-auto">
            {rewards.slice(0, 3).map((reward) => (
              <div
                className="relative flex aspect-square h-full flex-col items-center justify-center gap-1 overflow-hidden bg-custom-gradient p-2 md:px-3"
                key={reward.label}
              >
                <Image
                  alt={reward.label}
                  className="z-20 w-16 object-contain md:w-20 lg:w-24"
                  height={80}
                  src={reward.imagePath}
                  width={80}
                />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
